/**
 * The stable CSS hooks a business can target when styling their own forms.
 *
 * Everything named here is a public contract: once a hook ships, the markup
 *  that emits it has to keep emitting it. Tailwind utility classes and our
 *  internal class names (.form-section, .choose-block, .is-error, ...) are
 *  explicitly *not* part of that contract and are free to change.
 *
 * docs/form-css.md documents these for businesses, and FormCssApiTest keeps
 *  the two from drifting apart.
 */

export const FORM_CSS_API_VERSION = 2;

/**
 * Scope root. Custom CSS gets rewritten to sit underneath this class, so
 *  anything that should be themeable has to live inside it.
 */
export const FORM_ROOT_CLASS = 'breely-form';

/**
 * Named pieces of the page, emitted as data-breely-part. A part name is only
 *  unique within its context, ex. every question has its own "label".
 */
export const FormPart = {
    // Note: the page background is deliberately absent. It sits outside the
    //  scope root so that Breely's mark can live there, out of reach of a
    //  business's own CSS
    HEADER: 'header',
    LOGO: 'logo',
    CONTAINER: 'container',
    PROGRESS: 'progress',
    FORM_TITLE: 'form-title',
    BACK: 'back',
    FORM: 'form',
    LABEL: 'label',
    REQUIRED_MARKER: 'required-marker',
    DESCRIPTION: 'description',
    CONTROL: 'control',
    INPUT: 'input',
    OPTION: 'option',
    OPTION_INPUT: 'option-input',
    OPTION_LABEL: 'option-label',
    ERROR: 'error',
    PACKAGE_BALANCE_LINK: 'package-balance-link',
    FORM_ERROR: 'form-error',
    NEXT: 'next',
    SUBMIT: 'submit',

    // Uploading a file
    UPLOAD_BUTTON: 'upload-button',
    UPLOAD_FILE_NAME: 'upload-file-name',
    UPLOAD_REMOVE: 'upload-remove',

    // Scheduling: picking a date and time
    AVAILABILITY: 'availability',
    CALENDAR: 'calendar',
    DATE_NAVIGATION: 'date-navigation',
    NO_AVAILABILITY: 'no-availability',
    TIME_PANEL: 'time-panel',
    TIME_PANEL_HEADING: 'time-panel-heading',
    TIME_OPTIONS: 'time-options',
    TIME_OPTION: 'time-option',
    TIMEZONE: 'timezone',
    DAY_HEADING: 'day-heading',

    // Choosing how many of something
    STEPPER: 'stepper',
    STEPPER_DECREASE: 'stepper-decrease',
    STEPPER_VALUE: 'stepper-value',
    STEPPER_INCREASE: 'stepper-increase',

    // Scheduling: picking what is being booked
    SELECTION_LIST: 'selection-list',
    SELECTION_SUBTITLE: 'selection-subtitle',
    SELECTION_ACTION: 'selection-action',
    SELECTION_GROUP_HEADING: 'selection-group-heading',
    SELECTION_BLOCK: 'selection-block',
    SELECTION_SELECTED: 'selection-selected',
    SELECTION_CHANGE: 'selection-change',
    SELECTION_IMAGE: 'selection-image',
    SELECTION_TITLE: 'selection-title',
    SELECTION_PRICE: 'selection-price',
    SELECTION_DESCRIPTION: 'selection-description',

    // Buying a gift certificate
    GIFT_CERTIFICATE_PREVIEW: 'gift-certificate-preview',
    GIFT_CERTIFICATE_IMAGES: 'gift-certificate-images',
    GIFT_CERTIFICATE_IMAGE: 'gift-certificate-image',

    // Signing in to a client account
    ACCOUNT_TITLE: 'account-title',
    ACCOUNT_DESCRIPTION: 'account-description',
    ACCOUNT_BUTTON: 'account-button',

    // The thank you step, after a form is submitted
    THANK_YOU_ICON: 'thank-you-icon',
    THANK_YOU_TITLE: 'thank-you-title',
    THANK_YOU_DESCRIPTION: 'thank-you-description',
    THANK_YOU_BUTTON: 'thank-you-button',

    // Classes
    CLASS_LIST: 'class-list',
    CLASS_BLOCK: 'class-block',
    CLASS_TIME: 'class-time',
    CLASS_TITLE: 'class-title',
    CLASS_SPOTS: 'class-spots',
    CLASS_BOOK: 'class-book',
    CLASS_IMAGE: 'class-image',
    CLASS_DURATION: 'class-duration',
    CLASS_RESOURCE: 'class-resource',

    // The month grid a class schedule can be shown in
    CLASS_CALENDAR_WEEKDAY: 'class-calendar-weekday',
    CLASS_CALENDAR_DAY: 'class-calendar-day',
    CLASS_CALENDAR_DAY_NUMBER: 'class-calendar-day-number',
} as const;

export type FormPartName = (typeof FormPart)[keyof typeof FormPart];

export type FormRootState = 'filling' | 'payment' | 'thank-you';

export type FormSectionState = 'current' | 'visible' | 'hidden';

/**
 * Attributes render as "true" or not at all, so both [data-breely-error] and
 *  [data-breely-error='true'] work as selectors
 */
type BooleanAttribute = 'true' | undefined;

function flag(value: unknown): BooleanAttribute {
    return value ? 'true' : undefined;
}

function id(value: unknown): string | undefined {
    return value === null || value === undefined || value === ''
        ? undefined
        : `${value}`;
}

/** Marks an element as a named part, ex. {...part(FormPart.LABEL)} */
export function part(name: FormPartName) {
    return { 'data-breely-part': name };
}

/**
 * States a part can be in, on top of whatever part it is
 */
export const FormStateName = {
    SELECTED: 'selected',
    FULL: 'full',
    TODAY: 'today',
    UNAVAILABLE: 'unavailable',
} as const;

export type FormState = (typeof FormStateName)[keyof typeof FormStateName];

/**
 * Flags the states an element is in, skipping the ones it isn't, ex.
 *  {...state(isSelected && FormStateName.SELECTED)}
 */
export function state(...names: (FormState | false | null | undefined)[]) {
    const attributes: Record<string, 'true'> = {};

    for (const name of names) {
        if (name) {
            attributes[`data-breely-${name}`] = 'true';
        }
    }

    return attributes;
}

/**
 * Days in the availability calendar are rendered by react-calendar, which only
 *  lets us hand tiles class names — so dates are the one place the contract is
 *  classes rather than data attributes
 */
export const DateClass = {
    DATE: 'breely-date',
    SELECTED: 'breely-date-selected',
    UNAVAILABLE: 'breely-date-unavailable',
    TODAY: 'breely-date-today',
} as const;

export function dateClassNames({
    isSelected,
    isUnavailable,
    isToday,
}: {
    isSelected?: boolean;
    isUnavailable?: boolean;
    isToday?: boolean;
}): string[] {
    return [
        DateClass.DATE,
        ...(isSelected ? [DateClass.SELECTED] : []),
        ...(isUnavailable ? [DateClass.UNAVAILABLE] : []),
        ...(isToday ? [DateClass.TODAY] : []),
    ];
}

export function formRootAttributes({
    objectType,
    objectId,
    state,
    step,
    isEmbedded,
}: {
    objectType?: string;
    objectId?: string | number;
    state?: FormRootState;
    step?: number;
    isEmbedded?: boolean;
}) {
    return {
        'data-breely-css-version': `${FORM_CSS_API_VERSION}`,
        'data-breely-object-type': objectType,
        'data-breely-object': id(objectId),
        'data-breely-state': state,
        'data-breely-step': id(step),
        'data-breely-embedded': flag(isEmbedded),
    };
}

export function sectionAttributes({
    sectionId,
    index,
    state,
}: {
    sectionId: string | number;
    index: number;
    state: FormSectionState;
}) {
    return {
        'data-breely-section': id(sectionId),
        'data-breely-section-index': `${index}`,
        'data-breely-section-state': state,
    };
}

export function questionAttributes({
    questionId,
    type,
    isRequired,
    hasError,
    isAnswered,
}: {
    questionId?: string | number;
    type?: string;
    isRequired?: boolean;
    hasError?: boolean;
    isAnswered?: boolean;
}) {
    return {
        'data-breely-question': id(questionId),
        'data-breely-question-type': type,
        'data-breely-required': flag(isRequired),
        'data-breely-error': flag(hasError),
        'data-breely-answered': flag(isAnswered),
    };
}

/**
 * One choice within a question. The value is the business's own option text,
 *  never an answer, so it is safe to expose as a selector
 */
export function optionAttributes({
    value,
    isSelected,
}: {
    value?: string | number;
    isSelected?: boolean;
}) {
    return {
        ...part(FormPart.OPTION),
        'data-breely-option': id(value),
        'data-breely-selected': flag(isSelected),
    };
}

/**
 * Anything carrying a hook someone could write a rule against. Used to walk up
 *  from whatever the mouse is actually over, which is usually a plain wrapper
 *  or a piece of admin chrome rather than something styleable.
 */
export const HOOK_SELECTOR =
    '[data-breely-part],[data-breely-question-type],[data-breely-section-index]';

export type InspectedHook = {
    element: Element;
    /** Every element with this hook, anywhere on the form */
    selector: string;
    /** The same hook, but only within questions of this type */
    inQuestionType?: string;
    /** The same hook, but only within this one question */
    inQuestion?: string;
};

/**
 * The hook nearest to an element, and the selectors that reach it.
 *
 * The broad form comes first — every element with that hook, on every question
 *  — which is what someone styling a form usually wants. The narrower forms
 *  are offered alongside it, since the same hook is shared by questions of
 *  different types and the difference is invisible from the name alone.
 */
export function inspectElement(target: Element | null): InspectedHook | null {
    const element = target?.closest?.(HOOK_SELECTOR);

    if (!element) {
        return null;
    }

    const selector = selectorForHook(element);

    if (!selector) {
        return null;
    }

    const question = element.closest('[data-breely-question]');

    if (!question) {
        // Ex. the submit button or the form title, which belong to no question
        return { element, selector };
    }

    const questionId = question.getAttribute('data-breely-question');
    const questionType = question.getAttribute('data-breely-question-type');
    const isTheQuestionItself = question === element;

    return {
        element,
        selector,
        // The question's own wrapper is already addressed by its type, so
        //  repeating it would just restate the broad selector
        inQuestionType:
            !isTheQuestionItself && questionType
                ? `[data-breely-question-type='${questionType}'] ${selector}`
                : undefined,
        inQuestion: questionId
            ? isTheQuestionItself
                ? `[data-breely-question='${questionId}']`
                : `[data-breely-question='${questionId}'] ${selector}`
            : undefined,
    };
}

function selectorForHook(element: Element): string | null {
    const part = element.getAttribute('data-breely-part');

    if (part) {
        return `[data-breely-part='${part}']`;
    }

    const questionType = element.getAttribute('data-breely-question-type');

    if (questionType) {
        return `[data-breely-question-type='${questionType}']`;
    }

    const sectionIndex = element.getAttribute('data-breely-section-index');

    if (sectionIndex) {
        return `[data-breely-section-index='${sectionIndex}']`;
    }

    return null;
}

/** Whether a question has an answer, for [data-breely-answered] */
export function isAnswered(value: unknown): boolean {
    if (
        value === null ||
        value === undefined ||
        value === '' ||
        value === false
    ) {
        return false;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value as object).length > 0;
    }

    return true;
}
